import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { container } from "tsyringe";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import $ from "jquery";
import { FilterFaqs } from "../../infrastructure/utils/filterFaqs";
import DetailsContainer from "./details/detailsContainer";
import LoadingDetails from "./results/loading/loadingDetails";
import { BabySeatOption } from "../../domain/model/availability/offersData";
import { preloadStripe } from "../../infrastructure/stripe/stripeLoader";
import { searchTimeout } from "../../infrastructure/utils/withTimeout";
import { buildVehicleUrl } from "../../utils/buildVehicleUrl";
import "./css/custom_new.css";
import "./css/new-product-information.css";
import "../assets/saas/pages/product-details.scss";
// Preload Stripe.js on first user interaction (scroll, touch, mouse).
// By then the initial page load is done and the network is free.
if (typeof window !== 'undefined') {
    const triggerPreload = () => {
        console.log("[Stripe] Preloading on user interaction");
        preloadStripe();
        ['scroll', 'touchstart', 'mousemove'].forEach((e) => window.removeEventListener(e, triggerPreload));
    };
    ['scroll', 'touchstart', 'mousemove'].forEach((e) => window.addEventListener(e, triggerPreload, { once: true, passive: true }));
}
/**
 * Normalizes baby seat fields on a raw offer object so downstream components
 * receive consistent property names (babySeatType, pricePerUnit, currency)
 * regardless of the backend field naming (seatType, unitPrice, currencyCode).
 */
const normalizeOfferBabySeats = (offer) => {
    var _a, _b, _c, _d, _e;
    if (!offer)
        return offer;
    const rawOptions = (_d = (_c = (_b = (_a = offer.babySeatOptions) !== null && _a !== void 0 ? _a : offer.babySeatAddOns) !== null && _b !== void 0 ? _b : offer.babySeatAddons) !== null && _c !== void 0 ? _c : offer.babySeats) !== null && _d !== void 0 ? _d : [];
    return {
        ...offer,
        babySeatOptions: rawOptions.map((b) => new BabySeatOption(b)),
        hasBabySeatAddons: (_e = offer.hasBabySeatAddons) !== null && _e !== void 0 ? _e : rawOptions.length > 0,
    };
};
const ProductDetails = () => {
    var _a, _b, _c;
    const [completeReservation, setCompleteReservation] = useState(false);
    const { t, i18n } = useTranslation();
    const [myData, setData] = useState([]);
    const [offerVariants, setOfferVariants] = useState(() => {
        try {
            const stored = sessionStorage.getItem("allInclusiveOffer");
            return stored ? { allInclusive: JSON.parse(stored) } : {};
        }
        catch (_a) {
            return {};
        }
    });
    const [isAllInclusiveOffer, setIsAllInclusiveOffer] = useState(false);
    const [userAnalyzer, setUserAnalyzer] = useState(null);
    const [collections, setCollections] = useState([]);
    const [images, setImages] = useState([]);
    const [rate, setRate] = useState(0);
    const [applyDiscount, setApplyDiscount] = useState(false);
    const [userUFP, setUserUFP] = useState('');
    const [localCurrency, setLocalCurrency] = useState(false);
    const [ip, setIp] = useState("");
    const [localCurrencyVisible, setLocalCurrencyVisible] = useState(false);
    const [extraDiscount, setExtraDiscount] = useState(0);
    const [email, setEmail] = useState(sessionStorage.getItem("email") || "");
    const [orderNumber, setOrderNumber] = useState("");
    const [dataSplitPayment, setDataSplitPayment] = useState({
        initialSplitPaymentAmount: 0,
        showSplitPayment: false,
        showSplitPaymentAllInclusive: false,
        minDaysInAdvance: 0,
        secondPaymentDate: "",
        remainingSplitPaymentAmount: 0,
        initialSplitPaymentAmountAllInclusive: 0,
        remainingSplitPaymentAmountAllInclusive: 0,
    });
    let secret = 0;
    const market = sessionStorage.getItem("market") || "us";
    const filterFaqs = new FilterFaqs();
    const searchParams = new URLSearchParams(window.location.search);
    const app = searchParams.has("app");
    const rawParamCurrency = ((_a = document.cookie
        .split("; ")
        .find((row) => row.startsWith("currencyApp="))) === null || _a === void 0 ? void 0 : _a.split("=")[1]) || searchParams.get("securityCurrency") || searchParams.get("currency");
    // Guard against invalid values like "undefined" or empty strings from URL params
    const paramCurrency = rawParamCurrency && /^[A-Z]{3}$/i.test(rawParamCurrency.trim()) ? rawParamCurrency.trim().toUpperCase() : null;
    const rawSessionCurrency = sessionStorage.getItem("currency");
    const sessionCurrency = rawSessionCurrency && /^[A-Z]{3}$/i.test(rawSessionCurrency.trim()) ? rawSessionCurrency.trim().toUpperCase() : null;
    const rawShopifyCurrency = (_c = (_b = window.Shopify) === null || _b === void 0 ? void 0 : _b.currency) === null || _c === void 0 ? void 0 : _c.active;
    const shopifyCurrency = rawShopifyCurrency && /^[A-Z]{3}$/i.test(rawShopifyCurrency) ? rawShopifyCurrency.toUpperCase() : null;
    const getExchangeRates = container.resolve("iUseCaseGetExchangeRates");
    const getCollections = container.resolve("iUseCaseGetCollections");
    const bookingServices = container.resolve("IBookingServices");
    const [formData, setFormData] = useState({
        pickUpDate: searchParams.get("pickUpDate") || "",
        dropOffDate: searchParams.get("dropOffDate") || "",
        pickUpTime: searchParams.get("pickUpTime") || "",
        dropOffTime: searchParams.get("dropOffTime") || "",
        pickUpOfficeCode: searchParams.get("pickUpOfficeCode") || "",
        dropOffOfficeCode: searchParams.get("dropOffOfficeCode") || "",
        totalDays: searchParams.get("days") || "",
        currency: paramCurrency || sessionCurrency || shopifyCurrency || "USD",
        userId: searchParams.get("userId") || "",
        offerId: searchParams.get("id") || "",
        transactionId: "",
        idSession: "",
        pickupName: searchParams.get("pickupName") || "",
        dropoffName: searchParams.get("dropoffName") || "",
        paymentOnDestination: searchParams.get("paymentOnDestination") || "",
    });
    // Invalidate stale checkout state when the offer changed
    // (user came back to a different car or different dates).
    const previousOfferId = sessionStorage.getItem("offerId");
    if (previousOfferId && previousOfferId !== formData.offerId) {
        sessionStorage.removeItem("onCheckout");
        sessionStorage.removeItem("checkout_intent_id_paypal");
    }
    sessionStorage.setItem("days", formData.totalDays || "");
    sessionStorage.setItem("currency", formData.currency || "");
    sessionStorage.setItem("userId", formData.userId || "");
    sessionStorage.setItem("offerId", formData.offerId || "");
    const showDiscountModal = () => setCompleteReservation(true);
    const extractDate = (dateTime) => (dateTime === null || dateTime === void 0 ? void 0 : dateTime.split("T")[0]) || "";
    const extractTime = (dateTime) => {
        const timePart = dateTime === null || dateTime === void 0 ? void 0 : dateTime.split("T")[1];
        return timePart ? timePart.slice(0, 5) : "";
    };
    const updateFormDataFromOffer = (offer) => {
        if (!offer)
            return;
        setFormData((prev) => {
            var _a, _b, _c, _d;
            return ({
                ...prev,
                pickUpDate: extractDate(offer.pickUpDateTime),
                dropOffDate: extractDate(offer.returnDateTime),
                pickUpTime: extractTime(offer.pickUpDateTime),
                dropOffTime: extractTime(offer.returnDateTime),
                pickUpOfficeCode: ((_a = offer.pickUpLocation) === null || _a === void 0 ? void 0 : _a.locationCode) || "",
                dropOffOfficeCode: ((_b = offer.returnLocation) === null || _b === void 0 ? void 0 : _b.locationCode) || "",
                totalDays: offer.totalRentDays || "",
                userId: searchParams.get("userId") || "",
                offerId: offer.id || searchParams.get("id") || "",
                pickupName: ((_c = offer.pickUpLocation) === null || _c === void 0 ? void 0 : _c.locationName) || "",
                dropoffName: ((_d = offer.returnLocation) === null || _d === void 0 ? void 0 : _d.locationName) || "",
                paymentOnDestination: searchParams.get("paymentOnDestination") || "",
            });
        });
    };
    const handleOfferToggle = (checked) => {
        setIsAllInclusiveOffer(checked);
        const selectedOffer = checked && offerVariants.allInclusive ? offerVariants.allInclusive : offerVariants.standard;
        if (selectedOffer) {
            setData(selectedOffer);
            updateFormDataFromOffer(selectedOffer);
        }
    };
    const isFetching = useRef(false);
    // Restore allInclusive offer from sessionStorage to survive page refresh.
    const storedAllInclusive = (() => {
        try {
            const stored = sessionStorage.getItem("allInclusiveOffer");
            return stored ? JSON.parse(stored) : null;
        }
        catch (_a) {
            return null;
        }
    })();
    const allInclusiveOfferRef = useRef(storedAllInclusive);
    // Tracks the standard offer currently rendered. Used inside the WebSocket
    // callback to detect whether an incoming message is a legitimate baby-seat
    // enrichment (first-visit async flow from Avis / Alamo / Hertz) versus a
    // stale re-dispatch that would erase the baby seats we already show.
    const standardOfferRef = useRef(null);
    const asiaCountries = [
        "IN", "HK", "SG", "KR", "JP", "TR", "TH", "MY", "PH", "ID",
        "VN", "BD", "PK", "LK", "KH", "MM", "TW",
    ];
    const eurCountries = [
        "AL", "AD", "AT", "BE", "BA", "BG", "HR", "CZ", "DK", "EE",
        "FI", "GR", "HU", "IS", "IE", "LV", "LI", "LT", "LU", "MT",
        "MD", "MC", "ME", "NL", "NO", "PL", "RO", "RU", "SM", "RS",
        "SK", "SI", "SE", "CH", "UA", "VA",
    ];
    const caribeCountries = [
        "AG", "BS", "BB", "BZ", "BM", "VG", "KY", "CW", "DM", "GD",
        "GY", "HT", "JM", "SX", "LC", "VC", "TT", "TC",
    ];
    const africaCountries = [
        "AO", "DZ", "BJ", "BW", "BF", "BI", "CV", "CM", "TD", "KM",
        "CG", "CI", "EG", "SZ", "ET", "GA", "GM", "GH", "GN", "GW",
        "GQ", "KE", "LS", "LR", "LY", "MG", "MW", "ML", "MA", "MU",
        "MR", "MZ", "NA", "NE", "NG", "CF", "CD", "RW", "ST", "SN",
        "SC", "SL", "SO", "ZA", "SD", "SS", "TZ", "TG", "TN", "UG",
        "DJ", "ZM", "ZW", "ER",
    ];
    const emiratedCountries = [
        "BH", "CY", "IQ", "IL", "JO", "KW", "LB", "OM", "PS", "QA",
        "SA", "TR", "AE", "YE",
    ];
    /** Effects */
    useEffect(() => {
        if (extraDiscount !== 0)
            getProduct();
    }, [extraDiscount]);
    useEffect(() => {
        const collapsibleFAQS = document.getElementById("collapsibleFAQS");
        if (collapsibleFAQS) {
            collapsibleFAQS.style.height = "auto";
            collapsibleFAQS.style.opacity = "1";
        }
        // deactivate onbeforeunload
        window.onbeforeunload = null;
        filterFaqs.handle();
        // URL corrections
        // Redirect disabled for debugging UK/locale flow.
        // if (window.location.pathname === "/undefined-null/products/vehicle") {
        //   const params = window.location.href.split("?")[1];
        //   const newUrl = `${window.location.origin}/products/vehicle?${params}`;
        //   window.location.href = newUrl;
        // }
        if (!searchParams.has("app")) {
            const rawPathname = window.location.pathname;
            const pathname = rawPathname.toLowerCase();
            const pathSegment = pathname.split("/")[1] || "";
            const sessionLocale = (sessionStorage.getItem("locale") || "").toLowerCase();
            const supportedDialects = ["es-es", "en-uk", "en-gb"];
            const getBaseLocale = (rawLocale) => rawLocale.slice(0, 2);
            const resolveLocale = () => {
                const candidate = pathSegment && !["products", "pages"].includes(pathSegment) ? pathSegment : sessionLocale;
                if (candidate === "en")
                    return "";
                if (supportedDialects.includes(candidate))
                    return `/${candidate}`;
                const baseLocale = getBaseLocale(candidate);
                if (baseLocale && baseLocale !== "en")
                    return `/${baseLocale}`;
                return "";
            };
            const locale = resolveLocale();
            const targetPath = `${locale}/products/vehicle`.replace(/\/{2,}/g, "/").replace(/\/$/, "");
            const currentPath = rawPathname.replace(/\/$/, "");
            // Redirect disabled for debugging UK/locale flow.
            // if (/\/products\/vehicle$/i.test(currentPath) && currentPath.toLowerCase() !== targetPath) {
            //   const params = window.location.search;
            //   window.location.href = `${window.location.origin}${targetPath}${params}`;
            // }
        }
        return () => {
            sessionStorage.removeItem("offerId");
            sessionStorage.removeItem("price");
        };
    }, []);
    useEffect(() => {
        // Number input sanitization
        $(document).on("change", 'input[type="number"]', function () {
            this.value = this.value.replace(/[^0-9]+/g, "");
            if (this.value < 1)
                this.value = 0;
        });
        $(document).on("keypress", 'input[type="number"]', function (event) {
            return (event.which > 47 && event.which < 58) || event.which === 13;
        });
        if (secret === 0) {
            getExchangeRates.getExchangeRates(formData.currency || "").then((data) => {
                setRate(parseFloat(data.rates[formData.currency || ""]));
                sessionStorage.setItem("rate", data.rates[formData.currency || ""]);
            });
            // todo: remove 6 - ago no use in details car
            // getCollections.handle().then((data) => setCollections(data));
        }
        // WebSocket listener
        const handleConsoleInput = (input) => {
            if (input === "WebSocketConnection.onOpen")
                getProduct();
        };
        const handleConsoleMessage = (messageEvent) => handleConsoleInput(messageEvent.data);
        window.addEventListener("message", handleConsoleMessage);
        // Fallback: if the WebSocket opened before the listener was registered,
        // the "WebSocketConnection.onOpen" message was missed. Fire getProduct()
        // after a short delay if no data has arrived yet.
        const fallbackTimer = setTimeout(() => {
            if (myData.length === 0 && !isFetching.current) {
                getProduct();
            }
        }, 500);
        return () => {
            window.removeEventListener("message", handleConsoleMessage);
            clearTimeout(fallbackTimer);
        };
    }, []);
    // WI #8432 — Mobile browsers' native Share menu reads
    // <link rel="canonical"> and <meta property="og:url"> before falling back
    // to the address-bar URL. Shopify SSR emits both tags with a clean URL
    // (no query string), so a vehicle detail page shared via WhatsApp lost
    // every param. Patch both tags client-side whenever `formData` is
    // hydrated so the native Share menu sees the full canonical URL.
    // Only run on Shopify product or collection pages — never patch the tags
    // on other surfaces where the bundle may incidentally mount.
    useEffect(() => {
        const pathname = window.location.pathname;
        const isProductOrCollectionPage = pathname.includes("/products") || pathname.includes("/collections");
        if (!isProductOrCollectionPage)
            return;
        if (!(formData === null || formData === void 0 ? void 0 : formData.offerId))
            return;
        const fullUrl = buildVehicleUrl(formData);
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", fullUrl);
        let ogUrl = document.querySelector('meta[property="og:url"]');
        if (!ogUrl) {
            ogUrl = document.createElement("meta");
            ogUrl.setAttribute("property", "og:url");
            document.head.appendChild(ogUrl);
        }
        ogUrl.setAttribute("content", fullUrl);
    }, [formData]);
    /** Product Fetch */
    const getProduct = () => {
        if (isFetching.current)
            return;
        isFetching.current = true;
        secret = 1;
        if (searchParams.get("offerId")) {
            const payload = { offerId: searchParams.get("offerId") };
            bookingServices.searchSinglePayment(payload, null, (data) => {
                var _a, _b;
                isFetching.current = false;
                const reservation = data.reservation.statusActive[0].reservations[0];
                const baseOffer = normalizeOfferBabySeats(reservation.offer);
                const responseAllInclusive = normalizeOfferBabySeats((_a = reservation.allInclusiveOffer) !== null && _a !== void 0 ? _a : null);
                setOfferVariants(prev => {
                    const effectiveAllInclusive = responseAllInclusive !== undefined
                        ? responseAllInclusive
                        : prev.allInclusive;
                    // Keep refs and sessionStorage in sync so downstream reads avoid
                    // stale closures, the offer survives page refresh, and the
                    // enrichment guard can detect whether the current render already
                    // has baby seats.
                    allInclusiveOfferRef.current = effectiveAllInclusive;
                    standardOfferRef.current = baseOffer;
                    try {
                        if (effectiveAllInclusive) {
                            sessionStorage.setItem("allInclusiveOffer", JSON.stringify(effectiveAllInclusive));
                        }
                        else {
                            sessionStorage.removeItem("allInclusiveOffer");
                        }
                    }
                    catch ( /* ignore storage errors */_a) { /* ignore storage errors */ }
                    if (responseAllInclusive === undefined && prev.allInclusive) {
                        Sentry.addBreadcrumb({
                            category: "offer.variants",
                            message: "responseAllInclusive was undefined; preserved previous allInclusive from state",
                            level: "info",
                        });
                    }
                    return { standard: baseOffer, allInclusive: effectiveAllInclusive };
                });
                const effectiveAllInclusive = allInclusiveOfferRef.current;
                if (!effectiveAllInclusive && isAllInclusiveOffer) {
                    setIsAllInclusiveOffer(false);
                }
                const selectedOffer = isAllInclusiveOffer && effectiveAllInclusive ? effectiveAllInclusive : baseOffer;
                setData(selectedOffer);
                setEmail(reservation.email);
                const cleanedReservationCode = ((_b = reservation.reservationCode) === null || _b === void 0 ? void 0 : _b.replace(/^HOLA#/, "")) || "";
                setOrderNumber(cleanedReservationCode);
                updateFormDataFromOffer(selectedOffer);
            }, userUFP !== null && userUFP !== void 0 ? userUFP : sessionStorage.getItem("userFP"));
        }
        else {
            const payload = {
                searchSingleId: formData.offerId,
                userId: formData.userId,
                pickUpLocation: {
                    locationCode: formData.pickUpOfficeCode,
                    locationName: formData.pickupName,
                    codeContext: "IATA",
                    placeId: sessionStorage.getItem("pickPlaceId") || searchParams.get("pickPlaceId") || "",
                    dataSource: sessionStorage.getItem("pickDataSource") || searchParams.get("pickDataSource") || "",
                },
                returnLocation: {
                    locationCode: formData.dropOffOfficeCode,
                    locationName: formData.dropoffName,
                    codeContext: "IATA",
                    placeId: sessionStorage.getItem("dropPlaceId") || searchParams.get("dropPlaceId") || "",
                    dataSource: sessionStorage.getItem("dropDataSource") || searchParams.get("dropDataSource") || "",
                },
                language: i18n.language.toUpperCase(),
                market,
                visitorId: userUFP !== null && userUFP !== void 0 ? userUFP : sessionStorage.getItem("userFP"),
                extraDiscount,
                currency: formData.currency,
            };
            // WI #8534 — pass OfferId as an extra Sentry tag (via the new
            // `extraTags` parameter on `searchTimeout.start`) so timeout events for
            // single-availability are filterable by OfferId in Sentry.
            searchTimeout.start("single-availability", formData.transactionId || formData.offerId, undefined, undefined, { OfferId: formData.offerId });
            bookingServices.getAvailabilityById(payload, null, (data) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                searchTimeout.resolve("single-availability", formData.transactionId || formData.offerId);
                // Guard: decide which messages to accept after isFetching was cleared.
                //
                // Context: on first car-detail visit the backend may send TWO
                // SearchSingleResults messages for Avis / Alamo / Hertz:
                //   1. First message — arrives fast, has babySeatOptions: []
                //      (the async provider hasn't responded yet).
                //   2. Enrichment message — arrives later, carries the populated
                //      babySeatOptions produced by BabySeatResultUseCase.
                //
                // The previous guard (`if (!isFetching.current) return`) rejected
                // ALL messages after the first, which unintentionally discarded the
                // enrichment one — causing the baby seat section to never render on
                // first visit (users had to refresh).
                //
                // New rule:
                //   - Accept the first message (isFetching still true).
                //   - ALSO accept a later message when it brings baby seats we
                //     don't currently have — that's the legitimate enrichment.
                //   - Reject everything else (Momento 2 baby-seat-quotation
                //     re-dispatches, other stale callbacks) to preserve the
                //     existing protection against pricing contamination.
                const incomingOffer = data === null || data === void 0 ? void 0 : data.offer;
                // The backend may republish SearchSingle for the sibling offer in the
                // basic ↔ all-inclusive pair when its baby seats finish validating
                // asynchronously. Detect those messages by comparing the incoming
                // offer.id against the searchSingleId this page was opened with.
                // - Primary update (id matches): proceed with the full state update.
                // - Sibling update: refresh only that slot in offerVariants/refs so the
                //   user sees real baby seats when toggling, without touching myData,
                //   formData or triggering the chained resetQuotation in driverFormCard.
                const incomingId = incomingOffer === null || incomingOffer === void 0 ? void 0 : incomingOffer.id;
                const isPrimaryUpdate = !incomingId
                    || !payload.searchSingleId
                    || incomingId === payload.searchSingleId;
                if (!isPrimaryUpdate) {
                    const isSiblingAllInclusive = incomingId === ((_a = allInclusiveOfferRef.current) === null || _a === void 0 ? void 0 : _a.id);
                    const isSiblingStandard = incomingId === ((_b = standardOfferRef.current) === null || _b === void 0 ? void 0 : _b.id);
                    if (!isSiblingAllInclusive && !isSiblingStandard)
                        return;
                    const normalizedSibling = normalizeOfferBabySeats(incomingOffer);
                    setOfferVariants(prev => {
                        if (isSiblingAllInclusive) {
                            allInclusiveOfferRef.current = normalizedSibling;
                            try {
                                if (normalizedSibling) {
                                    sessionStorage.setItem("allInclusiveOffer", JSON.stringify(normalizedSibling));
                                }
                            }
                            catch ( /* ignore storage errors */_a) { /* ignore storage errors */ }
                            return { ...prev, allInclusive: normalizedSibling };
                        }
                        standardOfferRef.current = normalizedSibling;
                        return { ...prev, standard: normalizedSibling };
                    });
                    return;
                }
                const incomingHasBabySeats = Array.isArray(incomingOffer === null || incomingOffer === void 0 ? void 0 : incomingOffer.babySeatOptions)
                    && incomingOffer.babySeatOptions.length > 0;
                const currentHasBabySeats = Array.isArray((_c = standardOfferRef.current) === null || _c === void 0 ? void 0 : _c.babySeatOptions)
                    && standardOfferRef.current.babySeatOptions.length > 0;
                const isBabySeatEnrichment = !isFetching.current && incomingHasBabySeats && !currentHasBabySeats;
                if (!isFetching.current && !isBabySeatEnrichment)
                    return;
                const { initialSplitPaymentAmount = 0, showSplitPayment = false, showSplitPaymentAllInclusive, minDaysInAdvance = 0, remainingSplitPaymentAmount = 0, secondPaymentDate = "", errorCode = 0, initialSplitPaymentAmountAllInclusive = 0, remainingSplitPaymentAmountAllInclusive = 0, } = data;
                const normalizedShowSplitPaymentAllInclusive = (_d = showSplitPaymentAllInclusive !== null && showSplitPaymentAllInclusive !== void 0 ? showSplitPaymentAllInclusive : data.ShowSplitPaymentAllInclusive) !== null && _d !== void 0 ? _d : false;
                if (errorCode === 1) {
                    // redirect to home page
                    // Todo:: pending implement a proper 500 page
                    window.location.href = "/";
                    return;
                }
                setDataSplitPayment({ initialSplitPaymentAmount, showSplitPayment, showSplitPaymentAllInclusive: normalizedShowSplitPaymentAllInclusive, minDaysInAdvance, secondPaymentDate, remainingSplitPaymentAmount, initialSplitPaymentAmountAllInclusive, remainingSplitPaymentAmountAllInclusive });
                isFetching.current = false;
                const baseOffer = normalizeOfferBabySeats(data.offer);
                const responseAllInclusive = normalizeOfferBabySeats((_e = data.allInclusiveOffer) !== null && _e !== void 0 ? _e : null);
                // Preserve existing allInclusive offer when the response does not include
                // the field (e.g. baby-seat quotation SearchSingle responses only carry
                // the active rate type and omit allInclusiveOffer).
                setOfferVariants(prev => {
                    const effectiveAllInclusive = responseAllInclusive !== undefined
                        ? responseAllInclusive
                        : prev.allInclusive;
                    // Keep refs and sessionStorage in sync so downstream reads (including
                    // the enrichment guard above) avoid stale closures and the offer
                    // survives page refresh.
                    allInclusiveOfferRef.current = effectiveAllInclusive;
                    standardOfferRef.current = baseOffer;
                    try {
                        if (effectiveAllInclusive) {
                            sessionStorage.setItem("allInclusiveOffer", JSON.stringify(effectiveAllInclusive));
                        }
                        else {
                            sessionStorage.removeItem("allInclusiveOffer");
                        }
                    }
                    catch ( /* ignore storage errors */_a) { /* ignore storage errors */ }
                    if (responseAllInclusive === undefined && prev.allInclusive) {
                        Sentry.addBreadcrumb({
                            category: "offer.variants",
                            message: "responseAllInclusive was undefined; preserved previous allInclusive from state",
                            level: "info",
                        });
                    }
                    return { standard: baseOffer, allInclusive: effectiveAllInclusive };
                });
                const effectiveAllInclusive = allInclusiveOfferRef.current;
                if (!effectiveAllInclusive && isAllInclusiveOffer) {
                    setIsAllInclusiveOffer(false);
                }
                const selectedOffer = isAllInclusiveOffer && effectiveAllInclusive ? effectiveAllInclusive : baseOffer;
                setImages(baseOffer.car.images);
                setFormData((prev) => ({
                    ...prev,
                    totalDays: selectedOffer.totalRentDays,
                    transactionId: data.transactionId,
                    idSession: data.idSession,
                    pickupName: selectedOffer.pickUpLocation.locationCode,
                    dropoffName: selectedOffer.returnLocation.locationCode,
                }));
                setUserAnalyzer(data.userAnalyzer);
                setData(selectedOffer);
                const collapsibleFAQS = document.getElementById("collapsibleFAQS");
                if (collapsibleFAQS) {
                    collapsibleFAQS.style.height = "auto";
                    collapsibleFAQS.style.opacity = "1";
                }
                sessionStorage.setItem("price", (_k = (_j = (_h = (_g = (_f = selectedOffer.pricesDetail) === null || _f === void 0 ? void 0 : _f.currency) === null || _g === void 0 ? void 0 : _g[0]) === null || _h === void 0 ? void 0 : _h.total) === null || _j === void 0 ? void 0 : _j.baseAmount) !== null && _k !== void 0 ? _k : "0");
                setApplyDiscount(sessionStorage.getItem("applyDiscount") === "true");
                if (formData.currency !== "USD") {
                    //setLocalCurrencyVisible(data.showLocalPayment);
                    //setLocalCurrency(data.showLocalPayment);
                }
                setIp(data.locationInfo.ip);
            }, userUFP !== null && userUFP !== void 0 ? userUFP : sessionStorage.getItem("userFP"));
        }
    };
    /** Render */
    if (myData === null || myData === void 0 ? void 0 : myData.car) {
        return (_jsx(DetailsContainer, { myData: myData, images: images, collections: collections, formData: formData, rate: rate, checkout: false, componentType: "details", t: t, applyDiscount: applyDiscount, userUFP: userUFP, localCurrency: localCurrency, setLocalCurrency: setLocalCurrency, localCurrencyVisible: localCurrencyVisible, setLocalCurrencyVisible: setLocalCurrencyVisible, ip: ip, completeReservation: completeReservation, setCompleteReservation: setCompleteReservation, dataSplitPayment: dataSplitPayment, searchParams: searchParams, setEmail: setEmail, email: email, orderNumber: orderNumber, setOrderNumber: setOrderNumber, userAnalyzer: userAnalyzer, isAllInclusiveOffer: isAllInclusiveOffer, onAllInclusiveOfferChange: handleOfferToggle, allInclusiveOffer: offerVariants.allInclusive, standardOffer: offerVariants.standard }));
    }
    else {
        return _jsx(LoadingDetails, {});
    }
};
export default ProductDetails;
